import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";

type HallEndPage = "customer-info" | "visit-date";
// type HallInPath = 'ESTIMATE' | 'VISIT' | 'PROMOTION' | 'ETC';
// type HallDataNotObjectKey = 'inpath' | 'partnerUuids';
// type HallDataObjectKey = 'hopeWedding' | 'expectedPerson' | 'hopeVisit' | 'customer';
// type HallDataKey = HallDataNotObjectKey | HallDataObjectKey;
// type HallDataNestedKey = 'yearMonths' | 'days' | 'times' | 'additionalInquiry' | 'alternativeSchedules' | 'alternativeSchedules' |
// 						'min' | 'max' | 'adjustable' | 'dates' | 'role' | 'name' | 'tel' | 'mateName' | 'mateTel';
export interface BookingHallState {
  origin: string | null;
  endPage: HallEndPage;
  isComplete: boolean;
  hallName: string | null;
  data: {
    inpath: string; // 'ESTIMATE' | 'VISIT' | 'PROMOTION' | 'ETC';
    hopeWedding: {
      yearMonths: string[];
      days: string[];
      times: string[];
      additionalInquiry: string;
      alternativeSchedules: string[];
    };
    expectedPerson: {
      min: number | null;
      max: number | null;
      adjustable: number | null;
    };
    partnerUuids: string[];
    hopeVisit: {
      additionalInquiry: string;
      dates: { date: string; order: number }[];
    };
    customer: {
      role: "GROOM" | "BRIDE" | "";
      name: string;
      tel: string;
      mateName: string;
      mateTel: string;
    };
  };
}

const initialState = {
  origin: null,
  endPage: "customer-info",
  isComplete: false,
  hallName: null,
  data: {
    inpath: "ESTIMATE",
    hopeWedding: {
      yearMonths: [],
      days: [],
      times: [],
      additionalInquiry: "",
      alternativeSchedules: [],
    },
    expectedPerson: {
      min: null,
      max: null,
      adjustable: null,
    },
    partnerUuids: [],
    hopeVisit: {
      additionalInquiry: "",
      dates: [],
    },
    customer: {
      role: "",
      name: "",
      tel: "",
      mateName: "",
      mateTel: "",
    },
  },
} as BookingHallState;

const bookingHallSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    setOrigin(state, action: PayloadAction<{ origin: string }>) {
      state.origin = action.payload.origin;
    },
    setEndPage(state, action: PayloadAction<{ page: HallEndPage }>) {
      state.endPage = action.payload.page;
    },
    setIsComplete(state, action: PayloadAction<{ bool: boolean }>) {
      state.isComplete = action.payload.bool;
    },
    setInPath(state, action: PayloadAction<{ inpath: string }>) {
      state.data.inpath = action.payload.inpath;
    },
    setData(state, action: PayloadAction<{ data: any }>) {
      state.data = action.payload.data;
    },
    setPartnerUuids(state, action: PayloadAction<{ uuid: string | string[] }>) {
      const { uuid } = action.payload;
      const uuids = state.data.partnerUuids;
      if (Array.isArray(uuid)) {
        // 쿼리 파라미터와 같은 방식으로 한번에 1개 이상 선택
        state.data.partnerUuids = uuid;
      } else {
        // 예약 웨딩홀 선택화면에서 하나씩 선택
        if (uuids.indexOf(uuid) > -1) {
          state.data.partnerUuids = uuids.filter((u) => u !== uuid);
        } else {
          state.data.partnerUuids.push(uuid);
        }
      }
    },
    setYearMonths(state, action: PayloadAction<{ date: string }>) {
      const { date } = action.payload;
      const yearMonths = state.data.hopeWedding.yearMonths;
      if (date === "") {
        state.data.hopeWedding.yearMonths = [];
      } else {
        if (yearMonths.indexOf(date) > -1) {
          state.data.hopeWedding.yearMonths = yearMonths.filter((d) => d !== date);
        } else {
          if (state.data.hopeWedding.yearMonths.length >= 3) return;
          // 3개 이상 선택 불가
          state.data.hopeWedding.yearMonths.push(date);
        }
      }
    },
    setDays(state, action: PayloadAction<{ day: string }>) {
      const { day } = action.payload;
      const days = state.data.hopeWedding.days;

      if (day === "") {
        state.data.hopeWedding.days = [];
        return;
      } else {
        if (days.indexOf(day) > -1) {
          state.data.hopeWedding.days = days.filter((d) => d !== day);
        } else {
          state.data.hopeWedding.days.push(day);
        }
      }
    },
    clearHopeWedding(state) {
      state.data.hopeWedding = initialState.data.hopeWedding;
    },
    setTime(state, action: PayloadAction<{ time: string }>) {
      const { time } = action.payload;
      const times = state.data.hopeWedding.times;
      if (time === "") {
        state.data.hopeWedding.times = [];
        return;
      } else {
        if (times.indexOf(time) > -1) {
          state.data.hopeWedding.times = times.filter((t) => t !== time);
        } else {
          state.data.hopeWedding.times.push(time);
        }
      }
    },
    setUpdateTimeLabel(state, action: PayloadAction<{ times: string[] }>) {
      const { times } = action.payload;
      state.data.hopeWedding.times = times;
    },
    setAlternativeSchedules(state, action: PayloadAction<{ schedule: string | string[] }>) {
      const { schedule } = action.payload;
      const alternativeSchedules = state.data.hopeWedding.alternativeSchedules;
      if (typeof schedule === "string") {
        // 하나씩 선택
        if (alternativeSchedules.indexOf(schedule) > -1) {
          state.data.hopeWedding.alternativeSchedules = alternativeSchedules.filter(
            (s) => s !== schedule,
          );
        } else {
          state.data.hopeWedding.alternativeSchedules.push(schedule);
        }
      } else {
        // 한번에 업데이트
        state.data.hopeWedding.alternativeSchedules = schedule;
      }
    },
    setExpectedPerson(state, action: PayloadAction<{ min: number | null; max: number | null }>) {
      const { min, max } = action.payload;
      console.log("setExpectedPerson", min, max);
      if (!state.data.expectedPerson) {
        state.data.expectedPerson = {
          min: null,
          max: null,
          adjustable: null,
        };
      }
      state.data.expectedPerson.min = min;
      state.data.expectedPerson.max = max;
      console.log("state.data.expectedPerson", state.data.expectedPerson);
    },
    setAdjustable(state, action: PayloadAction<{ adjustable: number }>) {
      state.data.expectedPerson.adjustable = action.payload.adjustable;
    },
    setDates(state, action: PayloadAction<{ arr: { date: string; order: number }[] }>) {
      state.data.hopeVisit.dates = action.payload.arr;
    },
    setAdditionalInquiry(
      state,
      action: PayloadAction<{ key: "hopeWedding" | "hopeVisit"; data: string }>,
    ) {
      const { key, data } = action.payload;
      state.data[key].additionalInquiry = data;
    },
    setCustomer(
      state,
      action: PayloadAction<{ key: keyof typeof initialState.data.customer; data: string }>,
    ) {
      const { key, data } = action.payload;
      if (key === "role") {
        state.data.customer[key] = data as "GROOM" | "BRIDE";
      } else {
        state.data.customer[key] = data;
      }
    },
    setHallName(state, action: PayloadAction<{ hallName: string }>) {
      state.hallName = action.payload.hallName;
    },
  },
});

export const {
  setEndPage,
  setOrigin,
  setIsComplete,
  setInPath,
  setData,
  setPartnerUuids,
  setYearMonths,
  setDays,
  setTime,
  clearHopeWedding,
  setUpdateTimeLabel,
  setAlternativeSchedules,
  setExpectedPerson,
  setAdjustable,
  setDates,
  setAdditionalInquiry,
  setCustomer,
  setHallName,
} = bookingHallSlice.actions;
export const bookingHall = (state: RootState) => state.bookingHall;
export const bookingHallEndPage = (state: RootState) => state.bookingHall.endPage;
export const bookingHallData = (state: RootState) => state.bookingHall.data;
export default bookingHallSlice.reducer;
